import React, { useState } from 'react'; // Import useState
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaRegSmileBeam } from "react-icons/fa";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";


const ContactPage = () => {
  // Define formData state with explicit types
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '', // Matches 'businessEmail' in FormSubmissionDto
    phone: '', // Matches 'phone' in FormSubmissionDto
    companyName: '',
    interests: [] as string[], // Explicitly define interests as string[]
    howDidYouFindUs: '',
    subscribeNewsletter: false,
  });

  // State for submission status messages
  const [submissionStatus, setSubmissionStatus] = useState('');

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement; // Cast to HTMLInputElement for 'checked' property
      const checked = target.checked;

      if (name === 'subscribeNewsletter') {
        setFormData(prev => ({ ...prev, [name]: checked }));
      } else { // Handle multiple interests checkboxes
        setFormData(prev => {
          const newInterests = checked
            ? [...prev.interests, value]
            : prev.interests.filter((interest: string) => interest !== value);
          return { ...prev, interests: newInterests }; // Ensure 'interests' remains string[]
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    setSubmissionStatus('Submitting...'); // Show submitting status

    try {
      // Corrected API endpoint to match FormController.java's @PostMapping("/submit-form")
      // Use port 8082 as per your application.properties
      const response = await fetch('https://www.nadezhda.in/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData), // Send formData as JSON
      });

      if (response.ok) {
        setSubmissionStatus('Form submitted successfully!');
        // Clear the form after successful submission
        setFormData({
            firstName: '',
            lastName: '',
            businessEmail: '',
            phone: '',
            companyName: '',
            interests: [],
            howDidYouFindUs: '',
            subscribeNewsletter: false,
        });
      } else {
        const errorText = await response.text();
        setSubmissionStatus(`Submission failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus('An error occurred during submission.');
    }
  };


  return (
    <div className=" min-h-screen">
      <Helmet>
  <title>Contact Us | Nadezhda Venture - Best IT Training in Noida</title>
  <meta
    name="description"
    content="Get in touch with Nadezhda Venture for the best IT training and services in Noida. Reach us by phone, email, or visit our office."
  />
  <meta name="keywords" content="Contact Nadezhda Venture, IT Training Noida, Get in Touch, Tech Services India" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://www.nadezhdaventure/contact" />
</Helmet>

      <div className="relative">
  {/* CTA Section */}
  <section className="relative min-h-[400px] flex items-center justify-center bg-gray-900 text-white text-center overflow-hidden px-4 md:px-10 lg:px-20">

    {/* Background Image */}
    <img
      src="https://ambilio.com/wp-content/uploads/2022/12/contact-us-banner-edited.jpeg"
      alt="CTA Background"
      className="absolute inset-0 w-full h-full object-cover z-0"
    />


    {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />



    {/* Animated Foreground Content */}
    <div className="relative z-10 max-w-3xl -mt-32">
      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="text-3xl md:text-4xl font-bold text-orange-400 mb-4"
      >
        Ready to Build Your Career?
      </motion.h2>

      <motion.p
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="text-lg text-gray-200 mb-6"
      >
        Let’s discuss your ideas and turn them into reality. We’re just one click away.
      </motion.p>

      <motion.div
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
  viewport={{ once: true }}
  className="text-center px-4"
>
  <div className="inline-flex items-center justify-center gap-3 text-2xl font-bold bg-gradient-to-r from-orange-500 via-blue-500 to-orange-500 bg-clip-text text-transparent drop-shadow-md">
    <FaRegSmileBeam className="text-3xl text-pink-400 animate-pulse -mt-6" />
    We truly appreciate your interest in Nadezhda Venture Pvt. Ltd.
  </div>


</motion.div>
    </div>
  </section>
</div>



      {/* Contact Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10">
        {/* Left Side - Map/Image */}
        <motion.div
          className="rounded-lg overflow-hidden shadow-xl"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <iframe
            className="w-full h-full min-h-[400px]"
            src="https://www.google.com/maps/embed?pb=!1m18..." // Replace with real embed
            loading="lazy"
            title="Location"
          ></iframe>
        </motion.div>

        {/* Right Side - Form */}
        <motion.div
          className="bg-blue-900 text-white p-8 rounded-xl shadow-2xl"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-6 text-orange-400">
            Upgrade to Nadezhda Venture Pvt. Lt. Support Today
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4 text-sm md:text-base"> {/* Add onSubmit handler */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input type="text" name="firstName" placeholder="First name*" className="p-3 rounded text-black w-full" required value={formData.firstName} onChange={handleChange} />
              <input type="text" name="lastName" placeholder="Last name*" className="p-3 rounded text-black w-full" required value={formData.lastName} onChange={handleChange} />
              <input type="email" name="businessEmail" placeholder="Business Email*" className="p-3 rounded text-black w-full" required value={formData.businessEmail} onChange={handleChange} />
              <input type="tel" name="phone" placeholder="Phone*" className="p-3 rounded text-black w-full" required value={formData.phone} onChange={handleChange} />
            </div>

            <input type="text" name="companyName" placeholder="Company Name*" className="p-3 rounded text-black w-full" required value={formData.companyName} onChange={handleChange} />

            <fieldset className="text-orange-300 font-semibold">
              <legend className="mb-2">I'm Interested In:</legend>
              <div className="grid grid-cols-2 gap-2 text-white">
                <label><input type="checkbox" name="interests" value="Virtual Staffing" className="mr-2" checked={formData.interests.includes('Virtual Staffing')} onChange={handleChange} />Virtual Staffing</label>
                <label><input type="checkbox" name="interests" value="Cybersecurity" className="mr-2" checked={formData.interests.includes('Cybersecurity')} onChange={handleChange} />Cybersecurity</label>
                <label><input type="checkbox" name="interests" value="IT Services" className="mr-2" checked={formData.interests.includes('IT Services')} onChange={handleChange} />IT Services</label>
                <label><input type="checkbox" name="interests" value="IT Consulting" className="mr-2" checked={formData.interests.includes('IT Consulting')} onChange={handleChange} />IT Consulting</label>
                <label><input type="checkbox" name="interests" value="Other" className="mr-2" checked={formData.interests.includes('Other')} onChange={handleChange} />Other</label>
              </div>
            </fieldset>

            <input type="text" name="howDidYouFindUs" placeholder="How did you find out about us?*" className="p-3 rounded text-black w-full" required value={formData.howDidYouFindUs} onChange={handleChange} />

            <label className="block mt-2">
              <input type="checkbox" name="subscribeNewsletter" className="mr-2" checked={formData.subscribeNewsletter} onChange={handleChange} />
              Yes, subscribe me to your newsletter.
            </label>

            <button
              type="submit"
              className="mt-4 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-6 rounded w-full transition duration-300"
            >
              Submit
            </button>
            {submissionStatus && (
              <p className="mt-4 text-center text-sm">
                {submissionStatus}
              </p>
            )}
          </form>
        </motion.div>

      </div>

        {/* Contact Info Section */}

        <div className="text-center mt-10 group">
  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight transition-all duration-300">
    <span className="text-blue-900">Upgrade to </span>
    <span className="text-orange-400">Nadezhda</span>
    <span className="text-orange-400">IT Support </span>
    <span className="text-blue-900">Today</span>
  </h2>
  <div className="w-48 h-1 mx-auto mt-2 transition-all duration-300 " />
</div>


<div className="px-4 py-10 bg-gray-50">
  <motion.div
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    viewport={{ once: true, amount: 0.2 }}
    className="p-8 rounded-xl max-w-6xl mx-auto"
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {/* Email Card */}
      <motion.div
        whileHover={{
          scale: 1.05,
          boxShadow: "0 15px 25px rgba(0, 0, 0, 0.15)",
          transition: { duration: 0.3 },
        }}
        className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer transition-all duration-300 transform hover:outline hover:outline-4 hover:outline-white hover:outline-offset-2"
      >
        <div className="flex justify-center items-center mb-4">
          <FaEnvelope className="text-orange-500 text-4xl" />
        </div>
        <h3 className="text-blue-900 font-semibold text-lg mb-2">Email</h3>
        <p className="text-gray-700 text-sm break-words">info@nadezhda.in</p>
      </motion.div>

      {/* Phone Card */}
      <motion.div
        whileHover={{
          scale: 1.05,
          boxShadow: "0 15px 25px rgba(0, 0, 0, 0.15)",
          transition: { duration: 0.3 },
        }}
        className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer transition-all duration-300 transform hover:outline hover:outline-4 hover:outline-white hover:outline-offset-2"
      >
        <div className="flex justify-center items-center mb-4">
          <FaPhone className="text-orange-500 text-4xl" />
        </div>
        <h3 className="text-blue-900 font-semibold text-lg mb-2">Phone</h3>
        <p className="text-gray-700 text-sm">+91 9412220200 , +91 120 4582913</p>
      </motion.div>

      {/* Address Card */}
      <motion.div
        whileHover={{
          scale: 1.05,
          boxShadow: "0 15px 25px rgba(0, 0, 0, 0.15)",
          transition: { duration: 0.3 },
        }}
        className="bg-white rounded-xl shadow-md p-6 text-center cursor-pointer transition-all duration-300 transform hover:outline hover:outline-4 hover:outline-white hover:outline-offset-2"
      >
        <div className="flex justify-center items-center mb-4">
          <FaMapMarkerAlt className="text-orange-500 text-4xl" />
        </div>
        <h3 className="text-blue-900 font-semibold text-lg mb-2">Address</h3>
        <p className="text-gray-700 text-sm">Block-B Sector-2 Noida </p>
      </motion.div>
    </div>
  </motion.div>

</div>
    </div>

  );
};

export default ContactPage;